import type { FraudAlertRow, ScoreData } from "../../types/tabs";

/** Coerce a single object / array / scalar into an array of rows. */
export function fraudAlertsAsRows(value: unknown): FraudAlertRow[] {
  if (value == null) return [];
  if (Array.isArray(value)) return value as FraudAlertRow[];
  if (typeof value === "object") return [value as FraudAlertRow];
  return [{ code: "", description: String(value) }];
}

export function fraudRowHasContent(row: FraudAlertRow): boolean {
  if (!row || typeof row !== "object") return false;
  const c = row.code;
  const d = row.description;
  const codeOk = c != null && String(c).trim() !== "";
  const descOk = d != null && String(d).trim() !== "";
  return codeOk || descOk;
}

export function getFraudIdScanAlertRows(scoreData: ScoreData): FraudAlertRow[] {
  return fraudAlertsAsRows(scoreData?.fraud_alerts?.fraudIDScanAlertCodes).filter(
    fraudRowHasContent,
  );
}

export function getFraudSocialNumAlertRows(scoreData: ScoreData): FraudAlertRow[] {
  return fraudAlertsAsRows(scoreData?.fraud_alerts?.fraudSocialNumAlertCode).filter(
    fraudRowHasContent,
  );
}

export function getFraudIdentityScanRows(scoreData: ScoreData): FraudAlertRow[] {
  return fraudAlertsAsRows(scoreData?.fraud_alerts?.identityScan).filter(
    fraudRowHasContent,
  );
}

export function getFraudVictimIndicator(scoreData: ScoreData): FraudAlertRow | null {
  const v = scoreData?.fraud_alerts?.fraudVictimIndicator;
  if (v == null || typeof v !== "object") return null;
  if (Array.isArray(v)) {
    const first = v.length ? v[0] : null;
    return first && fraudRowHasContent(first) ? first : null;
  }
  return fraudRowHasContent(v as FraudAlertRow) ? (v as FraudAlertRow) : null;
}

export function hasFraudAlerts(scoreData: ScoreData): boolean {
  if (!scoreData?.fraud_alerts || typeof scoreData.fraud_alerts !== "object") {
    return false;
  }
  return (
    getFraudIdScanAlertRows(scoreData).length > 0 ||
    getFraudSocialNumAlertRows(scoreData).length > 0 ||
    getFraudIdentityScanRows(scoreData).length > 0 ||
    getFraudVictimIndicator(scoreData) != null
  );
}

/** API may send `{}` for `denied_checkbox_data` — truthy but renders an empty pink panel. */
export function deniedCheckboxDataHasDisplayableEntries(data: unknown): boolean {
  if (data == null) return false;
  if (typeof data === "string") return data.trim().length > 0;
  if (Array.isArray(data)) return data.length > 0;
  if (typeof data === "object") {
    return Object.keys(data as Record<string, unknown>).length > 0;
  }
  return false;
}
